import { PROFESSION_MAP } from '@constants/profession';
import { createSlice } from '@reduxjs/toolkit';
import type {
  StepsInterface,
  UserInterface,
} from '@store/user/interfaces/userInterfaces';
import { convertCountryNamesToCodes } from '@utils/countryUtils';

import type { ApiUserResponse } from '../../../services/api/userApi';
import {
  getUserByIdThunk,
  submitFinanceStepThunk,
  submitJobStepThunk,
  submitPersonalStepThunk,
  submitReferencesStepThunk,
  submitResidenceStepThunk,
  submitValidationStepThunk,
} from '../../../services/thunks/userThunks';
import { formatPhoneWithCountryCode } from '../../../utils/phoneUtils';

// Type for thunk rejection payload
interface ThunkErrorPayload {
  message: string;
  status?: number;
  code?: string;
}

// Initial steps configuration based on your routes
const initialSteps: StepsInterface = {
  validation: {
    id: 'validation',
    name: 'Validación de datos',
    route: '/validation',
    isCompleted: false,
    order: 1,
  },
  personal: {
    id: 'personal',
    name: 'Información personal',
    route: '/personal',
    isCompleted: false,
    order: 2,
  },
  residence: {
    id: 'residence',
    name: 'Información de residencia',
    route: '/residence',
    isCompleted: false,
    order: 3,
  },
  finance: {
    id: 'finance',
    name: 'Información financiera',
    route: '/finance',
    isCompleted: false,
    order: 4,
  },
  job: {
    id: 'job',
    name: 'Información laboral',
    route: '/job',
    isCompleted: false,
    order: 5,
  },
  references: {
    id: 'references',
    name: 'Referencias',
    route: '/references',
    isCompleted: false,
    order: 6,
  },
  confirm: {
    id: 'confirm',
    name: 'Confirmación',
    route: '/confirm',
    isCompleted: false,
    order: 7,
  },
  finish: {
    id: 'finish',
    name: 'Finalización',
    route: '/finish',
    isCompleted: false,
    order: 8,
  },
};

export const initialState: UserInterface = {
  id: '',
  authToken: null,
  tokenExpiresAt: null,
  personalInfo: {
    fullName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    nationality: '',
    documentType: 'Pasaporte',
    documentNumber: '',
    passportExpiry: '',
    passportIssueDate: '',
    civilStatus: '',
    gender: '',
  },
  fatcaInfo: {
    applies: false,
    usCitizen: undefined,
    hasGreenCard: undefined,
    stayed183: undefined,
    tin: '',
    taxCountry: '',
  },
  address: {
    street: '',
    city: '',
    state: '',
    residence: '',
    zipCode: '11101',
    country: 'Honduras',
    extraNationality: {
      applies: false,
      nationalityCRS: '',
      taxResidenceCountryCRS: '',
      hasTaxpayerNumber: undefined,
      taxpayerNumber: '',
      taxpayerNumberReason: '',
      taxpayerNumberExplanation: '',
    },
  },
  professionalInfo: {
    profession: '',
    companyName: '',
    position: '',
    workAddress: {
      street: '',
      residence: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
    monthlyIncome: 0,
    employmentStatus: 'employed',
    occupation: '',
    retiredCountry: '',
  },
  jobInfo: {
    companyName: '',
    initialDepositToOpenAccount: null,
    independentActivity: '',
    accountType: 'Ahorro',
    currency: 'Dólares',
    pepInfo: {
      isPEP: false,
      pepPosition: '',
      pepInstitution: '',
      pepPositionStartDate: '',
      pepPositionEndDate: '',
      familyMemberPEP: false,
      familyMemberPepRelation: '',
      familyMemberPepPosition: '',
      familyMemberPepDate: '',
    },
  },
  validation: {
    isValidated: false,
    validatedAt: '',
    validationErrors: [],
  },
  financeInfo: {
    monthlyIncome: null,
    variableIncome: null,
    countriesOfIncome: [],
    deposits: {
      type: 'Transferencias Internacionales',
      amount: null,
      transactionsQuantity: null,
      countriesOfOrigin: [],
    },
    withdrawals: {
      type: 'Transferencias Internacionales',
      amount: null,
      transactionsQuantity: null,
      countriesOfOrigin: [],
    },
  },
  references: [{ name: '', type: '' }],
  confirmation: {
    acceptedTerms: false,
  },
  steps: initialSteps,
  completedSteps: [],
  currentStepData: {},
  errors: {},
  readOnlyFields: [
    'personalInfo.fullName',
    'personalInfo.documentType',
    'personalInfo.documentNumber',
    'personalInfo.civilStatus',
    'personalInfo.email',
    'financeInfo.deposits.type',
    'financeInfo.withdrawals.type',
  ],
  isSubmitting: false,
  isComplete: false,
  isLoadingUser: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState: initialState,
  reducers: {
    updateUser(state, action: { payload: Partial<UserInterface> }) {
      Object.assign(state, action.payload);
    },

    updateUserId(state, action: { payload: string }) {
      state.id = action.payload;
    },

    setAuthToken(
      state,
      action: { payload: { token: string; expiresAt: string } },
    ) {
      state.authToken = action.payload.token;
      state.tokenExpiresAt = action.payload.expiresAt;
    },

    clearAuthToken(state) {
      state.authToken = null;
      state.tokenExpiresAt = null;

      state.steps.validation.isCompleted = false;
      delete state.steps.validation.completedAt;

      state.completedSteps = state.completedSteps.filter(
        step => step !== 'validation',
      );
    },

    clearErrors(state) {
      state.errors = {};
    },

    resetUser(state) {
      Object.assign(state, initialState);
    },

    setFieldReadOnly(state, action: { payload: string }) {
      const fieldPath = action.payload;
      if (!state.readOnlyFields.includes(fieldPath)) {
        state.readOnlyFields.push(fieldPath);
      }
    },

    setFieldEditable(state, action: { payload: string }) {
      const fieldPath = action.payload;
      state.readOnlyFields = state.readOnlyFields.filter(
        field => field !== fieldPath,
      );
    },

    setFieldsReadOnly(state, action: { payload: string[] }) {
      action.payload.forEach(fieldPath => {
        if (!state.readOnlyFields.includes(fieldPath)) {
          state.readOnlyFields.push(fieldPath);
        }
      });
    },

    setProfessionByCode(state, action: { payload: string | number }) {
      const code = String(action.payload ?? '').trim();
      state.professionalInfo.profession = code;
      state.professionalInfo.professionName = PROFESSION_MAP[code] ?? '';
    },

    populateUserFromApi(
      state,
      action: {
        payload: ApiUserResponse & { token?: string; expires_at?: string };
      },
    ) {
      const apiData = action.payload;

      state.id = apiData.id;

      // Token handling
      if (apiData.token && apiData.expires_at) {
        state.authToken = apiData.token;
        state.tokenExpiresAt = apiData.expires_at;
      }

      // Map personal data from API
      if (apiData.steps?.personal) {
        const personal = apiData.steps.personal;
        state.personalInfo = {
          ...state.personalInfo,
          fullName: personal.full_name || '',
          firstName: personal.first_name || '',
          lastName: personal.last_name || '',
          email: personal.email || '',
          gender: personal.gender || '',
          phone: formatPhoneWithCountryCode(personal.phone || ''),
          dateOfBirth: personal.date_of_birth || '',
          nationality: personal.nationality || '',
          documentType: personal.document_type || '',
          documentNumber: personal.document_number || '',
          passportExpiry: personal.passport_expiry || '',
          passportIssueDate: personal.passport_emission || '',
          civilStatus: personal.civil_status || '',
        };

        // Map FATCA info
        if (personal.fatca_info) {
          const taxCountryCode = personal.fatca_info.tax_country
            ? convertCountryNamesToCodes([
                personal.fatca_info.tax_country,
              ])[0] || personal.fatca_info.tax_country
            : '';

          state.fatcaInfo = {
            ...state.fatcaInfo,
            applies: personal.fatca_info.applies || false,
            usCitizen: personal.fatca_info.us_citizen,
            hasGreenCard: personal.fatca_info.has_green_card,
            stayed183: personal.fatca_info.stayed183,
            tin: personal.fatca_info.tin || '',
            taxCountry: taxCountryCode,
          };
        }

        //  Map profession data
        state.professionalInfo = {
          ...state.professionalInfo,
          profession: personal.profession_code || personal.profession || '',
          occupation: personal.occupation || '',
          professionName: personal.profession || '',
          retiredCountry: personal.retired_country || '',
        };
      }

      //  Map residence data from API
      if (apiData.steps?.residence) {
        const residence = apiData.steps.residence;
        state.address = {
          ...state.address,
          street: residence.street || '',
          city: residence.city || '',
          state: residence.state || '',
          residence: residence.residence || '',
          country: residence.country || '',
          zipCode: residence.zip_code || state.address.zipCode,
        };

        // Map extra nationality con conversión de países
        if (residence.extra_nationality) {
          // Convertir nationality_crs de nombre a código
          const nationalityCRSCode = residence.extra_nationality.nationality_crs
            ? convertCountryNamesToCodes([
                residence.extra_nationality.nationality_crs,
              ])[0] || residence.extra_nationality.nationality_crs
            : '';

          // Convertir tax_residence_country_crs de nombre a código

          const taxResidenceCRSCode = residence.extra_nationality
            .tax_residence_country_crs
            ? convertCountryNamesToCodes([
                residence.extra_nationality.tax_residence_country_crs,
              ])[0] || residence.extra_nationality.tax_residence_country_crs
            : '';

          state.address.extraNationality = {
            applies: residence.extra_nationality.applies || false,
            nationalityCRS: nationalityCRSCode,
            taxResidenceCountryCRS: taxResidenceCRSCode,
            hasTaxpayerNumber: residence.extra_nationality.has_taxpayer_number,
            taxpayerNumber: residence.extra_nationality.taxpayer_number || '',
            taxpayerNumberReason:
              residence.extra_nationality.taxpayer_number_reason || '',
            taxpayerNumberExplanation:
              residence.extra_nationality.taxpayer_number_explanation || '',
          };
        }
      }

      // Map references data from API
      if (
        apiData.steps?.references &&
        Array.isArray(apiData.steps.references)
      ) {
        state.references = apiData.steps.references.map(ref => ({
          name: ref.name || '',
          type: ref.type || '',
        }));
      }
      // Map finance data from API (ya tiene conversión de países)
      if (apiData.steps?.finance) {
        const finance = apiData.steps.finance;

        // Convertir nombres de países a códigos
        const countriesOfIncomeCodes = convertCountryNamesToCodes(
          finance.countries_of_income || [],
        );
        const depositsCountriesCodes = convertCountryNamesToCodes(
          finance.deposits?.countries_of_origin || [],
        );
        const withdrawalsCountriesCodes = convertCountryNamesToCodes(
          finance.withdrawals?.countries_of_origin || [],
        );

        state.financeInfo = {
          ...state.financeInfo,
          monthlyIncome: finance.monthly_income || null,
          variableIncome: finance.variable_income ?? null,
          countriesOfIncome: countriesOfIncomeCodes,
          deposits: {
            type: finance.deposits?.type || 'Transferencias Internacionales',
            amount: finance.deposits?.amount || null,
            transactionsQuantity:
              finance.deposits?.transactions_quantity || null,
            countriesOfOrigin: depositsCountriesCodes,
          },
          withdrawals: {
            type: finance.withdrawals?.type || 'Transferencias Internacionales',
            amount: finance.withdrawals?.amount || null,
            transactionsQuantity:
              finance.withdrawals?.transactions_quantity || null,
            countriesOfOrigin: withdrawalsCountriesCodes,
          },
        };
      }

      // Map job data from API
      if (apiData.steps?.job) {
        const job = apiData.steps.job;

        state.jobInfo = {
          ...state.jobInfo,
          companyName: job.company_name || '',
          initialDepositToOpenAccount:
            job.initial_deposit_to_open_account || null,
          independentActivity: job.independent_activity || '',
          accountType: job.account_type || 'Ahorro',
          currency: job.currency || 'Dólares',
        };

        // Map PEP info
        if (job.pep_info) {
          state.jobInfo.pepInfo = {
            isPEP: job.pep_info.is_p_e_p || job.pep_info.is_pep || false,
            pepPosition: job.pep_info.pep_position || '',
            pepInstitution: job.pep_info.pep_institution || '',
            pepPositionStartDate: job.pep_info.pep_position_start_date || '',
            pepPositionEndDate: job.pep_info.pep_position_end_date || '',
            familyMemberPEP:
              job.pep_info.family_member_p_e_p ||
              job.pep_info.family_member_pep ||
              false,
            familyMemberPepRelation:
              job.pep_info.family_member_pep_relation || '',
            familyMemberPepPosition:
              job.pep_info.family_member_pep_position || '',
            familyMemberPepDate: job.pep_info.family_member_pep_date || '',
          };
        }
      }

      // Map validation data from API
      if (apiData.steps?.validation) {
        const validation = apiData.steps.validation;
        state.validation = {
          ...state.validation,
          isValidated: validation.is_data_verified || false,
          validatedAt: validation.policy?.at || '',
        };

        // Mark validation step as completed if verified
        if (validation.is_biometric_verified || validation.is_data_verified) {
          state.steps.validation.isCompleted = true;
          state.steps.validation.completedAt =
            validation.policy?.at || new Date().toISOString();

          if (!state.completedSteps.includes('validation')) {
            state.completedSteps.push('validation');
          }
        }
      }

      // Update step completion status
      if (apiData.onboarding?.steps) {
        apiData.onboarding.steps.forEach(apiStep => {
          // NO skip validation anymore
          if (state.steps[apiStep.key as keyof typeof state.steps]) {
            if (apiStep.finished_at) {
              state.steps[apiStep.key as keyof typeof state.steps].isCompleted =
                true;
              state.steps[apiStep.key as keyof typeof state.steps].completedAt =
                apiStep.finished_at;

              if (!state.completedSteps.includes(apiStep.key)) {
                state.completedSteps.push(apiStep.key);
              }
            }
          }
        });
      }
    },
  },
  extraReducers: builder => {
    //  getUserById thunk
    builder
      .addCase(getUserByIdThunk.pending, state => {
        state.isLoadingUser = true;
        delete state.errors?.userFetch;
      })
      .addCase(getUserByIdThunk.fulfilled, (state, action) => {
        state.isLoadingUser = false;

        const apiData = action.payload.data;

        // Preparar payload para populateUserFromApi
        const populatePayload: ApiUserResponse & {
          token?: string;
          expires_at?: string;
        } = {
          id: apiData.id,
          onboarding: apiData.onboarding,
          steps: apiData.steps,
          token: apiData.token,
          expires_at: apiData.expires_at,
        };
        userSlice.caseReducers.populateUserFromApi(state, {
          payload: populatePayload,
        } as never);
      })
      .addCase(getUserByIdThunk.rejected, (state, action) => {
        state.isLoadingUser = false;
        state.errors = {
          ...state.errors,
          userFetch:
            (action.payload as ThunkErrorPayload)?.message ||
            'Failed to fetch user data',
        };
      });

    // Personal step submission
    builder
      .addCase(submitPersonalStepThunk.pending, state => {
        state.isSubmitting = true;
        delete state.errors?.submission;
      })
      .addCase(submitPersonalStepThunk.fulfilled, (state, _action) => {
        state.isSubmitting = false;
        state.steps.personal.isCompleted = true;
        state.steps.personal.completedAt = Date.now().toString();
        if (!state.completedSteps.includes('personal')) {
          state.completedSteps.push('personal');
        }
      })
      .addCase(submitPersonalStepThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.errors = {
          ...state.errors,
          submission:
            (action.payload as ThunkErrorPayload)?.message ||
            'Failed to submit personal step',
        };
      });

    // Residence step submission
    builder
      .addCase(submitResidenceStepThunk.pending, state => {
        state.isSubmitting = true;
        delete state.errors?.submission;
      })
      .addCase(submitResidenceStepThunk.fulfilled, (state, _action) => {
        state.isSubmitting = false;
        state.steps.residence.isCompleted = true;
        state.steps.residence.completedAt = Date.now().toString();
        if (!state.completedSteps.includes('residence')) {
          state.completedSteps.push('residence');
        }
      })
      .addCase(submitResidenceStepThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.errors = {
          ...state.errors,
          submission:
            (action.payload as ThunkErrorPayload)?.message ||
            'Failed to submit residence step',
        };
      });

    // Finance step submission
    builder
      .addCase(submitFinanceStepThunk.pending, state => {
        state.isSubmitting = true;
        delete state.errors?.submission;
      })
      .addCase(submitFinanceStepThunk.fulfilled, (state, _action) => {
        state.isSubmitting = false;
        state.steps.finance.isCompleted = true;
        state.steps.finance.completedAt = Date.now().toString();
        if (!state.completedSteps.includes('finance')) {
          state.completedSteps.push('finance');
        }
      })
      .addCase(submitFinanceStepThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.errors = {
          ...state.errors,
          submission:
            (action.payload as ThunkErrorPayload)?.message ||
            'Failed to submit finance step',
        };
      });

    // Job step submission
    builder
      .addCase(submitJobStepThunk.pending, state => {
        state.isSubmitting = true;
        delete state.errors?.submission;
      })
      .addCase(submitJobStepThunk.fulfilled, (state, _action) => {
        state.isSubmitting = false;
        state.steps.job.isCompleted = true;
        state.steps.job.completedAt = Date.now().toString();
        if (!state.completedSteps.includes('job')) {
          state.completedSteps.push('job');
        }
      })
      .addCase(submitJobStepThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.errors = {
          ...state.errors,
          submission:
            (action.payload as ThunkErrorPayload)?.message ||
            'Failed to submit job step',
        };
      });

    // References step submission
    builder
      .addCase(submitReferencesStepThunk.pending, state => {
        state.isSubmitting = true;
        delete state.errors?.submission;
      })
      .addCase(submitReferencesStepThunk.fulfilled, (state, _action) => {
        state.isSubmitting = false;
        state.steps.references.isCompleted = true;
        state.steps.references.completedAt = Date.now().toString();
        if (!state.completedSteps.includes('references')) {
          state.completedSteps.push('references');
        }
      })
      .addCase(submitReferencesStepThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.errors = {
          ...state.errors,
          submission:
            (action.payload as ThunkErrorPayload)?.message ||
            'Failed to submit references step',
        };
      });

    // Validation step submission
    builder
      .addCase(submitValidationStepThunk.pending, state => {
        state.isSubmitting = true;
        delete state.errors?.submission;
      })
      .addCase(submitValidationStepThunk.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.steps.validation.isCompleted = true;
        state.steps.validation.completedAt = Date.now().toString();
        if (!state.completedSteps.includes('validation')) {
          state.completedSteps.push('validation');
        }

        if (action.payload?.data) {
          const apiData = action.payload.data;
          state.id = apiData.id;

          if (apiData.steps?.personal) {
            const personal = apiData.steps.personal;
            state.personalInfo = {
              ...state.personalInfo,
              fullName: personal.full_name || '',
              firstName: personal.first_name || '',
              lastName: personal.last_name || '',
              email: personal.email || '',
              phone: formatPhoneWithCountryCode(personal.phone || ''),
              dateOfBirth: personal.date_of_birth || '',
              nationality: personal.nationality || '',
              documentType: personal.document_type || '',
              documentNumber: personal.document_number || '',
              passportExpiry: personal.passport_expiry || '',
              civilStatus: personal.civil_status || '',
            };
          }

          if (apiData.steps?.residence) {
            const residence = apiData.steps.residence;
            state.address = {
              ...state.address,
              street: residence.street || '',
              city: residence.city || '',
              state: residence.state || '',
              residence: residence.residence || '',
              country: residence.country || '',
            };
          }

          if (
            apiData.steps?.references &&
            Array.isArray(apiData.steps.references)
          ) {
            state.references = apiData.steps.references.map(ref => ({
              name: ref.name || '',
              type: ref.type || '',
            }));
          }

          if (apiData.steps?.validation) {
            const validation = apiData.steps.validation;
            state.validation = {
              ...state.validation,
              isValidated: validation.is_data_verified || false,
              validatedAt: validation.policy?.at || '',
            };
          }

          if (apiData.onboarding?.steps) {
            apiData.onboarding.steps.forEach(apiStep => {
              if (state.steps[apiStep.key as keyof typeof state.steps]) {
                state.steps[
                  apiStep.key as keyof typeof state.steps
                ].isCompleted = true;
                state.steps[
                  apiStep.key as keyof typeof state.steps
                ].completedAt = apiStep.started_at;

                if (!state.completedSteps.includes(apiStep.key)) {
                  state.completedSteps.push(apiStep.key);
                }
              }
            });
          }
        }
      })
      .addCase(submitValidationStepThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.errors = {
          ...state.errors,
          submission:
            (action.payload as ThunkErrorPayload)?.message ||
            'Failed to submit validation step',
        };
      });
  },
});

export const {
  updateUser,
  updateUserId,
  setAuthToken,
  clearAuthToken,
  clearErrors,
  resetUser,
  setFieldReadOnly,
  setFieldEditable,
  setFieldsReadOnly,
  setProfessionByCode,
  populateUserFromApi,
} = userSlice.actions;

export const userReducer = userSlice.reducer;
